import { useCallback } from 'react';
import { adminUsersService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { SystemUser } from '../types';

// Custom query options for admin users API
interface AdminUsersQueryOptions {
  pageSize?: number;
  page?: number;
  sort?: string;
}

export function useSystemUsers(
  options?: AdminUsersQueryOptions
): ResourceState<SystemUser> & { meta?: any } {
  // Stringify options to create stable dependency
  const optionsKey = JSON.stringify(options);
  
  const fetchFn = useCallback(
    () =>
      adminUsersService.getAll(options) as unknown as Promise<{ data: SystemUser[]; meta?: any }>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionsKey]
  );
  return useResourceCollection<SystemUser>(fetchFn);
}

export function useSystemUserById(
  id: string | null
): SingleResourceState<SystemUser> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (userId: number) =>
      adminUsersService.getById(userId) as unknown as Promise<SystemUser>,
    []
  );
  return useResourceById<SystemUser>(fetchFn, numericId);
}

export function useCreateSystemUser(
  onSuccess?: (data: SystemUser) => void
): MutationState<SystemUser> {
  const createFn = useCallback(
    (data: any) => adminUsersService.create(data) as unknown as Promise<SystemUser>,
    []
  );
  return useCreateResource<SystemUser>(createFn, onSuccess);
}

export function useUpdateSystemUser(
  onSuccess?: (data: SystemUser) => void
): MutationState<SystemUser> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      return adminUsersService.update(id, data) as unknown as Promise<SystemUser>;
    },
    []
  );
  return useUpdateResource<SystemUser>(updateFn, onSuccess);
}

export function useDeleteSystemUser(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const userId = data.id || data.documentId || data;
      return adminUsersService.delete(userId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
